/**
 * Auto Match Fields Utility
 *
 * Provides matching logic for automatically mapping schema fields
 * to available workflow variables based on naming patterns.
 */

import type { SchemaField } from '@/stores/schemas/schema-store';
import type { AvailableVariable } from '@/components/workflow/SchemaFieldMapper';

export type MatchType = 'exact' | 'case-insensitive' | 'naming-convention' | 'nested-partial' | 'none';
export type MatchConfidence = 'high' | 'medium' | 'none';

export interface MatchResult {
  schemaField: string;
  schemaFieldType: SchemaField['type'];
  sourcePath: string | null;
  matchType: MatchType;
  confidence: MatchConfidence;
  selected: boolean;
}

/**
 * Normalize a field name for comparison.
 * Converts camelCase to snake_case, handles hyphens and spaces.
 */
export function normalizeFieldName(name: string): string {
  return name
    // Handle camelCase: insert underscore before uppercase letters
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    // Replace hyphens and spaces with underscores
    .replace(/[-\s]/g, '_')
    // Convert to lowercase
    .toLowerCase()
    // Remove any leading/trailing underscores
    .replace(/^_+|_+$/g, '')
    // Collapse multiple underscores
    .replace(/_+/g, '_');
}

/**
 * Extract the last segment of a dot-notation path.
 * e.g., "triggerConfig.message.battery.voltage" → "voltage"
 */
export function getLastPathSegment(path: string): string {
  const parts = path.split('.');
  return parts[parts.length - 1];
}

/**
 * Extract the last two segments of a dot-notation path combined.
 * e.g., "triggerConfig.message.battery.voltage" → "battery_voltage"
 */
export function getLastTwoSegments(path: string): string {
  const parts = path.split('.');
  if (parts.length >= 2) {
    return `${parts[parts.length - 2]}_${parts[parts.length - 1]}`;
  }
  return parts[parts.length - 1];
}

/**
 * Find the best match for a single schema field from available variables.
 */
export function findMatchForField(
  schemaField: SchemaField,
  availableVariables: AvailableVariable[]
): MatchResult {
  const schemaFieldName = schemaField.name;
  const normalizedSchema = normalizeFieldName(schemaFieldName);

  let bestMatch: MatchResult = {
    schemaField: schemaFieldName,
    schemaFieldType: schemaField.type,
    sourcePath: null,
    matchType: 'none',
    confidence: 'none',
    selected: false,
  };

  for (const variable of availableVariables) {
    const variableName = variable.name;
    const normalizedVar = normalizeFieldName(variableName);
    const lastSegment = normalizeFieldName(getLastPathSegment(variableName));
    const lastTwoSegments = normalizeFieldName(getLastTwoSegments(variableName));

    // Priority 1: Exact match (highest confidence)
    if (variableName === schemaFieldName) {
      return {
        schemaField: schemaFieldName,
        schemaFieldType: schemaField.type,
        sourcePath: variableName,
        matchType: 'exact',
        confidence: 'high',
        selected: true,
      };
    }

    // Priority 2: Case-insensitive exact match
    if (normalizedVar === normalizedSchema) {
      return {
        schemaField: schemaFieldName,
        schemaFieldType: schemaField.type,
        sourcePath: variableName,
        matchType: 'case-insensitive',
        confidence: 'high',
        selected: true,
      };
    }

    // Priority 3: Naming convention match (camelCase ↔ snake_case)
    // This handles cases like "batteryVoltage" matching "battery_voltage"
    if (normalizedVar === normalizedSchema && bestMatch.matchType === 'none') {
      bestMatch = {
        schemaField: schemaFieldName,
        schemaFieldType: schemaField.type,
        sourcePath: variableName,
        matchType: 'naming-convention',
        confidence: 'high',
        selected: true,
      };
    }

    // Priority 4: Last segment match (for nested paths)
    // e.g., "triggerConfig.message.battery_voltage" matches "battery_voltage"
    if (lastSegment === normalizedSchema && bestMatch.confidence !== 'high') {
      bestMatch = {
        schemaField: schemaFieldName,
        schemaFieldType: schemaField.type,
        sourcePath: variableName,
        matchType: 'nested-partial',
        confidence: 'medium',
        selected: true,
      };
    }

    // Priority 5: Last two segments combined match
    // e.g., "payload.battery.voltage" matches "battery_voltage"
    if (lastTwoSegments === normalizedSchema && bestMatch.confidence === 'none') {
      bestMatch = {
        schemaField: schemaFieldName,
        schemaFieldType: schemaField.type,
        sourcePath: variableName,
        matchType: 'nested-partial',
        confidence: 'medium',
        selected: true,
      };
    }
  }

  return bestMatch;
}

/**
 * Find matches for all schema fields from available variables.
 */
export function findAllMatches(
  schemaFields: SchemaField[],
  availableVariables: AvailableVariable[]
): MatchResult[] {
  return schemaFields.map((field) => findMatchForField(field, availableVariables));
}

/**
 * Get summary statistics for match results.
 */
export function getMatchSummary(matches: MatchResult[]): {
  total: number;
  matched: number;
  highConfidence: number;
  mediumConfidence: number;
  unmatched: number;
} {
  const total = matches.length;
  const matched = matches.filter((m) => m.sourcePath !== null).length;
  const highConfidence = matches.filter((m) => m.confidence === 'high').length;
  const mediumConfidence = matches.filter((m) => m.confidence === 'medium').length;
  const unmatched = matches.filter((m) => m.sourcePath === null).length;

  return {
    total,
    matched,
    highConfidence,
    mediumConfidence,
    unmatched,
  };
}

/**
 * Get confidence badge color class for UI display.
 */
export function getConfidenceBadgeClass(confidence: MatchConfidence): string {
  switch (confidence) {
    case 'high':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'medium':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'none':
    default:
      return 'bg-gray-100 text-gray-500 border-gray-200';
  }
}

/**
 * Get human-readable label for match type.
 */
export function getMatchTypeLabel(matchType: MatchType): string {
  switch (matchType) {
    case 'exact':
      return 'Exact';
    case 'case-insensitive':
      return 'Case Match';
    case 'naming-convention':
      return 'Convention';
    case 'nested-partial':
      return 'Nested';
    case 'none':
    default:
      return 'No Match';
  }
}
